import React from "react";
import { Box, Typography, Card, Button } from "@mui/material";
import MonetaryText from "../../../components/MonetaryText";
import { useNavigate } from "react-router-dom";
import type { StepId } from "../adapaters/financialStatementMeta";
import { FinancialStatementTotalsRow } from "../domain/entities/financial";
import {
  EmploymentStatusCode,
  employmentStatuses,
} from "../domain/entities/employment";
import { HomeStatusCode, homeStatuses } from "../domain/entities/home";

interface ChangeProps {
  totals: FinancialStatementTotalsRow[];
  employmentStatus?: keyof EmploymentStatusCode;
  homeStatus?: keyof HomeStatusCode;
}

type FsMode = "new" | "existing" | "review" | "edit";

// Map totals index (as rendered) to step ids
const totalsIndexToStepId: ReadonlyArray<StepId> = [
  "income",
  "fixed",
  "flexible",
  "debts",
];

function Change({
  totals,
  employmentStatus,
  homeStatus,
}: Readonly<ChangeProps>) {
  const navigate = useNavigate();

  const goToStep = (step: StepId, _modeOverride?: FsMode) => {
    const search = _modeOverride ? `?mode=${_modeOverride}` : "";
    navigate(`/sfs/${step}${search}`, { replace: true });
  };

  const handleEditHomeDetails = () => {
    goToStep("about", "edit");
  };

  // Navigate to the corresponding totals section by index
  const handleEditTotals = (idx: number) => {
    const step = totalsIndexToStepId[idx];
    if (step) {
      goToStep(step);
    }
  };

  return (
    <Box>
      {/* Render home details section */}
      <Card sx={{ mb: 2, borderRadius: "0px" }}>
        <Box
          sx={{
            backgroundColor: "primary.dark",
            color: "primary.contrastText",
            fontWeight: "bold",
            fontSize: "14px",
            p: 1,
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>My Home Details</Typography>
        </Box>
        <Box sx={{ p: 1 }}>
          <Box sx={{ width: "50%", display: "inline-block" }}>
            <Typography fontSize={12}>Summary</Typography>
            <Typography fontWeight={"bold"} fontSize={14}>
              {`${employmentStatuses[employmentStatus ?? (employmentStatuses.UNK as keyof EmploymentStatusCode)]}, ${homeStatuses[homeStatus ?? (homeStatuses.UNK as keyof HomeStatusCode)]}...`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "50%",
              display: "inline-block",
              textAlign: "right",
            }}
          >
            <Button
              onClick={handleEditHomeDetails}
              variant="outlined"
              sx={{
                mt: -1.5,
                mb: 1,
                p: 0.5,
                width: {
                  xs: "150px",
                  sm: "160px",
                  md: "170px",
                  lg: "180px",
                  xl: "190px",
                },
                textTransform: "none",
                borderRadius: 0,
                borderColor: "primary.dark",
                color: "primary.dark",
                "&:hover": {
                  borderColor: "primary.light",
                  color: "primary.dark",
                },
              }}
            >
              Change
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Render totals section */}
      {totals
        ? totals.map((item, idx) => (
            <Card key={item.id} sx={{ mb: 2, borderRadius: "0px" }}>
              <Box
                sx={{
                  backgroundColor: "primary.dark",
                  color: "primary.contrastText",
                  fontWeight: "bold",
                  fontSize: "14px",
                  p: 1,
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  {`My ${item.name}`}
                </Typography>
              </Box>
              <Box sx={{ p: 1 }}>
                <Box sx={{ width: "50%", display: "inline-block" }}>
                  <Typography fontSize={12}>Total</Typography>
                  <Typography fontSize={18} fontWeight="bold">
                    <MonetaryText value={item.value} />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "50%",
                    display: "inline-block",
                    textAlign: "right",
                  }}
                >
                  <Button
                    onClick={() => handleEditTotals(idx)}
                    variant="outlined"
                    sx={{
                      mt: -1.5,
                      mb: 1,
                      p: 0.5,
                      width: {
                        xs: "150px",
                        sm: "160px",
                        md: "170px",
                        lg: "180px",
                        xl: "190px",
                      },
                      textTransform: "none",
                      borderRadius: 0,
                      borderColor: "primary.dark",
                      color: "primary.dark",
                      "&:hover": {
                        borderColor: "primary.light",
                        color: "primary.dark",
                      },
                    }}
                  >
                    Change
                  </Button>
                </Box>
              </Box>
            </Card>
          ))
        : null}
    </Box>
  );
}

export default Change;
